import React, { useCallback, useMemo } from 'react';
import {
    CheckboxGroup,
    Label as LabelUI,
    RadioButtonGroup,
    TextInput,
} from 'ui-library';
import ButtonRadioElement from '../AtomicStructure/atoms/ButtonRadioElement';
import ConfirmationDisclaimer from '../ConfirmationDisclaimer/ConfirmationDisclaimer';
import './QuestionnaireQuestion.css';

// Types for answers
interface Answer {
    Id: number | string;
    Text: string | number;
    Order?: number;
}

// Types for question data
interface QuestionData {
    Id: number | string;
    Text: string;
    Type?: string;
    Layout?: string;
    Answers?: Answer[];
    GroupDescription?: string;
    TextLength?: number;
}

// Props interface
interface QuestionnaireQuestionProps {
    data?: QuestionData;
    value?: number | string | number[];
    error?: string;
    onChange?: (payload: { questionId: number | string; value: any }) => void;
    defaultValue?: number[];
    disableDefaultValue?: boolean;
    radioRenderComponent?: (args: {
        val?: number | string | number[];
        desc: string;
        error?: string;
        options: { value: number | string; label: string | number; disabled: boolean; name: number | string }[];
        onChange: (val: any) => void;
    }) => React.ReactNode;
    dataAttrCy?: string;
    disabled?: boolean;
    radioButtonWrapperClassName?: string;
}

// Helper functions
const setOptions = (
    options: Answer[] = [],
    disabled = false,
    name: number | string = Math.ceil(Math.random() * 100000),
) => options.map(({ Id, Text }) => ({
    value: Id,
    label: Text,
    disabled,
    name,
}));

const setButtonRadioOptions = (options: Answer[] = []) => options.map(({ Id, Text }) => ({
    label: Text,
    id: Id,
}));

const setCheckBoxOptions = (
    options: Answer[] = [],
    defaultValue: number[] = [],
    disableDefaultValue?: boolean,
) => options.map(({ Id, Text, Order }) => ({
    value: Id,
    label: Text,
    disabled: disableDefaultValue && (defaultValue || []).find((i) => i === Id),
    position: Order,
}));

// Component
const QuestionnaireQuestion: React.FC<QuestionnaireQuestionProps> = (props) => {
    const {
        data,
        value,
        error,
        onChange = () => {},
        defaultValue = [],
        disableDefaultValue = false,
        radioRenderComponent,
        dataAttrCy,
        disabled,
        radioButtonWrapperClassName,
    } = props;

    const onValueChange = useCallback(
        (val: any) => {
            if (data) {
                onChange({
                    questionId: data.Id,
                    value: val,
                });
            }
        },
        [data?.Id, onChange],
    );

    const renderCheckbox = (isConditional: boolean) => (
        <fieldset className="question-group">
            {data && <legend className="Form-control-label">{data.Text}</legend>}
            <CheckboxGroup
                value={value}
                options={setCheckBoxOptions(data?.Answers || [], defaultValue, disableDefaultValue)}
                error={error}
                onChange={(val: any) => onValueChange(val)}
                size="big"
                defaultValue={defaultValue}
                isConditional={isConditional}
                isInQuestionnaire
            />
        </fieldset>
    );

    const question = useMemo(() => {
        switch (data?.Layout) {
            case 'ButtonRadio':
                return (
                    <ButtonRadioElement
                        label={data.Text}
                        error={error}
                        items={setButtonRadioOptions(data.Answers || [])}
                        value={value}
                        passData={(id: any) => onValueChange(id)}
                        dataAttrCy={dataAttrCy}
                        disabled={disabled}
                        wrapperClassName={radioButtonWrapperClassName || ''}
                    />
                );
            case 'Radio':
                return radioRenderComponent ? (
                    radioRenderComponent({
                        val: value,
                        desc: data.Text,
                        error,
                        options: setOptions(data.Answers || []),
                        onChange: (val: any) => onValueChange(val),
                    })
                ) : (
                    <fieldset>
                        {data && <legend className="Form-control-label">{data.Text}</legend>}
                        <RadioButtonGroup
                            value={value}
                            options={setOptions(data.Answers || [], disabled, data.Id)}
                            error={error}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onValueChange(+e.target.value)}
                            disabled={disabled}
                        />
                    </fieldset>
                );
            case 'Checkbox':
                return renderCheckbox(false);
            case 'ConditionalCheckbox':
                return renderCheckbox(true);
            case 'Toggle':
                return (
                    <fieldset className="question-group">
                        {data && (
                            <legend className="Form-control-label">{data.GroupDescription}</legend>
                        )}
                        <ConfirmationDisclaimer
                            value={value}
                            onChange={(val: boolean) => onValueChange(val ? data?.Answers?.[0]?.Id : undefined)}
                            error={error}
                            agreeOnText={data?.Answers?.[0]?.Text as string}
                            text={data.Text}
                        />
                    </fieldset>
                );
            default:
                if (data?.Type === 'FreeText') {
                    return (
                        <>
                            <LabelUI label={data?.Text} />
                            <TextInput
                                value={value}
                                error={error}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => onValueChange(event?.target?.value || '')}
                                maxLength={data?.TextLength || 2000}
                            />
                        </>
                    );
                }

                return null;
        }
    }, [
        data?.Answers,
        data?.Id,
        data?.Layout,
        data?.Type,
        error,
        value,
        data?.GroupDescription,
        dataAttrCy,
        disabled,
        onValueChange,
    ]);

    return <div className="questionnaire-question">{question}</div>;
};

export default QuestionnaireQuestion;
